import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Pagination, Select, Space, Table, Typography } from 'antd';
import { API_DASHBOARD_DATA } from '../../api/api';
import { formatIndianStyle } from '../../helper/arrayFormatter';
import { axiosPost } from '../../services/http.services';
import './DashboardService.css';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const YEAR_OPTIONS = [2025].map((year) => ({ label: `${year}`, value: year }));
const MONTH_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => ({
	label: `${month}`,
	value: month,
}));

const DashboardService = () => {
	const [data, setData] = useState([]);
	const [selectedYear, setSelectedYear] = useState(null);
	const [selectedMonth, setSelectedMonth] = useState(null);
	const [selectedDateRange, setSelectedDateRange] = useState(null);
	const [showOldBcVc, setShowOldBcVc] = useState(false);
	const [loading, setLoading] = useState(false);
	const [errorMsg, setErrorMsg] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [totalRecords, setTotalRecords] = useState(0);

	useEffect(() => {
		const loadData = async () => {
			setLoading(true);
			setErrorMsg('');
			try {
				const response = await axiosPost(API_DASHBOARD_DATA, {
					date_range: selectedDateRange,
					year: selectedYear,
					month: selectedMonth,
					page: currentPage,
					page_size: pageSize,
				});

				const result = response.data;
				setData(Array.isArray(result?.data) ? result.data : []);
				setTotalRecords(Number(result?.total_records) || 0);
			} catch (error) {
				console.error('Error fetching dashboard service data:', error);
				setData([]);
				setErrorMsg('Unable to load dashboard data right now. Please try again.');
				setTotalRecords(0);
			} finally {
				setLoading(false);
			}
		};

		loadData();
	}, [selectedDateRange, selectedYear, selectedMonth, currentPage, pageSize]);

	const allColumns = useMemo(() => ([
		{
			title: 'Date',
			dataIndex: 'date',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return new Date(a.date) - new Date(b.date);
			},
			render: (text) => {
				if (text === 'TOTAL') {
					return text;
				}
				const date = new Date(text);
				const day = String(date.getDate()).padStart(2, '0');
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const year = date.getFullYear();
				return `${day}-${month}-${year}`;
			},
		},
		{
			title: 'Old BC VC Success',
			dataIndex: 'old_bc_vc_issued_success_count',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.old_bc_vc_issued_success_count - b.old_bc_vc_issued_success_count;
			},
			render: (text) => formatIndianStyle(text),
		},
		{
			title: 'Old BC VC Fail',
			dataIndex: 'old_bc_vc_issued_fail_count',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.old_bc_vc_issued_fail_count - b.old_bc_vc_issued_fail_count;
			},
			render: (text) => formatIndianStyle(text),
		},
		{
			title: 'New BC VC Success',
			dataIndex: 'new_bc_vc_issued_success_count',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.new_bc_vc_issued_success_count - b.new_bc_vc_issued_success_count;
			},
			render: (text) => formatIndianStyle(text),
		},
		{
			title: 'New BC VC Fail',
			dataIndex: 'new_bc_vc_issued_fail_count',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.new_bc_vc_issued_fail_count - b.new_bc_vc_issued_fail_count;
			},
			render: (text) => formatIndianStyle(text),
		},
		{
			title: 'Migrated Success',
			dataIndex: 'migated_old_bc_vc_to_new_success',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.migated_old_bc_vc_to_new_success - b.migated_old_bc_vc_to_new_success;
			},
			render: (text) => formatIndianStyle(text),
		},
		{
			title: 'Migrated Fail',
			dataIndex: 'migated_old_bc_vc_to_new_fail',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.migated_old_bc_vc_to_new_fail - b.migated_old_bc_vc_to_new_fail;
			},
			render: (text) => formatIndianStyle(text),
		},
		{
			title: 'Direct Aadhar',
			dataIndex: 'direct_aadhar',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.direct_aadhar - b.direct_aadhar;
			},
			render: (text) => formatIndianStyle(text),
		},
		{
			title: 'Digilocker',
			dataIndex: 'digilocker',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.digilocker - b.digilocker;
			},
			render: (text) => formatIndianStyle(text),
		},
		{
			title: 'Total VC Success',
			dataIndex: 'total_vc_stats_success',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.total_vc_stats_success - b.total_vc_stats_success;
			},
			render: (text) => formatIndianStyle(text),
		},
		{
			title: 'Total VC Fail',
			dataIndex: 'total_vc_stats_fail',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.total_vc_stats_fail - b.total_vc_stats_fail;
			},
			render: (text) => formatIndianStyle(text),
		},
		{
			title: 'DigiLocker Aadhaar',
			dataIndex: 'digilocker_aadhaar',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.digilocker_aadhaar - b.digilocker_aadhaar;
			},
			render: (text) => (text === null || text === undefined ? '-' : formatIndianStyle(text)),
		},
		{
			title: 'DigiLocker DL',
			dataIndex: 'digilocker_dl',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.digilocker_dl - b.digilocker_dl;
			},
			render: (text) => (text === null || text === undefined ? '-' : formatIndianStyle(text)),
		},
		{
			title: 'Domestic Shares',
			dataIndex: 'domestic_shares',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.domestic_shares - b.domestic_shares;
			},
			render: (text) => (text === null || text === undefined ? '-' : formatIndianStyle(text)),
		},
		{
			title: 'Hub Spoke Shares',
			dataIndex: 'hub_spoke_shares',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.hub_spoke_shares - b.hub_spoke_shares;
			},
			render: (text) => (text === null || text === undefined ? '-' : formatIndianStyle(text)),
		},
		{
			title: 'Hub Spoke Scan Failures',
			dataIndex: 'hub_spoke_scan_failures',
			align: 'right',
			sorter: (a, b) => {
				if (a.date === 'TOTAL' || b.date === 'TOTAL') {
					return 0;
				}
				return a.hub_spoke_scan_failures - b.hub_spoke_scan_failures;
			},
			render: (text) => (text === null || text === undefined ? '-' : formatIndianStyle(text)),
		},
	]), []);

	const columns = useMemo(() => {
		if (showOldBcVc) {
			return allColumns;
		}
		return allColumns.filter((col) => col.title !== 'Old BC VC Success' && col.title !== 'Old BC VC Fail');
	}, [allColumns, showOldBcVc]);

	const handleDateChange = (dates) => {
		setCurrentPage(1);
		setSelectedDateRange(dates ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')] : null);
		setSelectedYear(null);
		setSelectedMonth(null);
	};

	const handleYearChange = (year) => {
		setCurrentPage(1);
		setSelectedYear(year);
		setSelectedDateRange(null);
	};

	const handleMonthChange = (month) => {
		setCurrentPage(1);
		setSelectedMonth(month);
		setSelectedDateRange(null);
	};

	const handlePaginationChange = (page, nextPageSize) => {
		setCurrentPage(page);
		setPageSize(nextPageSize);
	};

	const handleClearFilters = () => {
		setCurrentPage(1);
		setSelectedDateRange(null);
		setSelectedYear(null);
		setSelectedMonth(null);
	};

	return (
		<div className="dashboard-service-page">
			<div className="dashboard-service-title-wrap">
				<Title level={3} style={{ margin: 0 }}>Dashboard Service</Title>
			</div>

			<Space direction="horizontal" wrap style={{ width: '100%', marginBottom: 20 }}>
				<RangePicker onChange={handleDateChange} />
				<Select
					allowClear
					placeholder="Select Year"
					onChange={handleYearChange}
					value={selectedYear}
					style={{ width: 140 }}
					options={YEAR_OPTIONS}
				/>
				<Select
					allowClear
					placeholder="Select Month"
					onChange={handleMonthChange}
					value={selectedMonth}
					style={{ width: 140 }}
					options={MONTH_OPTIONS}
				/>
				<Button onClick={handleClearFilters}>Clear Filters</Button>
				<Button
					type={showOldBcVc ? 'primary' : 'default'}
					onClick={() => setShowOldBcVc((prev) => !prev)}
				>
					{showOldBcVc ? 'Hide' : 'Show'} Old VC
				</Button>
			</Space>

			{errorMsg ? (
				<Alert
					type="error"
					message={errorMsg}
					showIcon
					style={{ marginBottom: 14 }}
				/>
			) : null}

			<Table
				columns={columns}
				dataSource={data}
				loading={loading}
				rowKey="date"
				pagination={false}
				scroll={{ x: 'max-content' }}
				className="dashboard-service-table"
				size="middle"
				showSorterTooltip={false}
			/>

			<Pagination
				current={currentPage}
				pageSize={pageSize}
				total={totalRecords}
				onChange={handlePaginationChange}
				showSizeChanger
				pageSizeOptions={['50', '100', '200']}
				style={{ marginTop: 12 }}
			/>
		</div>
	);
};

export default DashboardService;